import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import api from "../api/api"; 

// Define branches + sub-branches
const branches = [
  { main: "Hospet", subs: [""] },
  { main: "Gangavathi", subs: [""] },
  { main: "Kurekuppa", subs: [""] },
  { main: "Danapur", subs: [""] },
  { main: "Raichur", subs: [""] },
  { main: "Ballari", subs: [""] },
  { main: "Rane", subs: ["Hospet", "Chitradurga"] },
  { main: "Tel Turbo", subs: ["Hospet", "Chitradurga", "Ballari"] },
  { main: "Wabco", subs: ["Hospet", "Chitradurga"] },
  { main: "Tata Batteries", subs: ["Hospet"] },
  { main: "Cummins", subs: ["Hospet", "Chitradurga"] },
  { main: "Mining", subs: ["Hospet"] },
  { main: "Engine Reconditioning", subs: ["Hospet"] },
  { main: "IQRA School", subs: ["Gangavathi"] },
];

const makeBranchKey = (main, sub) => (sub ? `${main}::${sub}` : main);

const AdminQRUpload = () => {
  const [selectedMain, setSelectedMain] = useState("");
  const [selectedSub, setSelectedSub] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [qrCodes, setQrCodes] = useState({}); // Map branchKey -> url

  const allBranchKeys = branches.flatMap(b =>
    b.subs.map(sub => ({ main: b.main, sub }))
  );

  useEffect(() => {
    async function fetchAllQRCodeURLs() {
      const codes = {};
      for (const { main, sub } of allBranchKeys) {
        const key = makeBranchKey(main, sub);
        try {
          const res = await api.get(`/branchqrcode/${encodeURIComponent(key)}`);
          if (res.data) {
            codes[key] = res.data.qrCodeImageURL;
          }
        } catch {}
      }
      setQrCodes(codes);
    }
    fetchAllQRCodeURLs();
  }, []);

  useEffect(() => {
    const branchObj = branches.find(b => b.main === selectedMain);
    if (branchObj && !branchObj.subs.includes(selectedSub)) {
      setSelectedSub(branchObj.subs.length === 1 ? branchObj.subs[0] : "");
    }
  }, [selectedMain]);

  const handleFileChange = e => setFile(e.target.files[0]);

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage("");

    if (!selectedMain) {
      setMessage("Please select a branch.");
      return;
    }
    const branchKey = makeBranchKey(selectedMain, selectedSub);

    if (!file) {
      setMessage("Please select an image file to upload.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("branchKey", branchKey);
      formData.append("qrCodeImage", file);

      const res = await api.post("/branchqrcode/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data) {
        setQrCodes(prev => ({ ...prev, [branchKey]: res.data.qrCodeImageURL }));
        setMessage("QR Code uploaded successfully!");
        setFile(null);
      } else {
        setMessage("Failed to upload QR Code.");
      }
    } catch {
      setMessage("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (main, sub) => {
    if (!window.confirm(`Are you sure you want to delete QR code for ${main}${sub ? " - " + sub : ""}?`))
      return;

    const branchKey = makeBranchKey(main, sub);
    try {
      const res = await api.delete(`/branchqrcode/${encodeURIComponent(branchKey)}`);
      if (res.status === 200) {
        setQrCodes(prev => {
          const copy = { ...prev };
          delete copy[branchKey];
          return copy;
        });
        setMessage(`QR Code for ${main}${sub ? " - " + sub : ""} deleted successfully.`);
      } else {
        setMessage(`Failed to delete QR Code for ${main}${sub ? " - " + sub : ""}.`);
      }
    } catch {
      setMessage("Network error.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto py-12 px-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 max-w-6xl mx-auto mb-8">
          <h2 className="pt-4 text-2xl sm:text-3xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Branch QR Code Management
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center justify-center" encType="multipart/form-data">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Main Branch</label>
              <select value={selectedMain} onChange={e => setSelectedMain(e.target.value)} className="w-full border px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 transition">
                <option value="">Select branch</option>
                {branches.map(b => <option key={b.main} value={b.main}>{b.main}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Sub-Branch</label>
              <select
                value={selectedSub}
                onChange={e => setSelectedSub(e.target.value)}
                disabled={!selectedMain || (branches.find(b => b.main === selectedMain)?.subs.length <= 1)}
                className="w-full border px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 transition"
              >
                {selectedMain && branches.find(b => b.main === selectedMain)?.subs.length > 1 ? (
                  <>
                    <option value="">Select sub-branch</option>
                    {branches.find(b => b.main === selectedMain).subs.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </>
                ) : (
                  <option value="">{selectedMain ? "Main branch" : "Select main branch first"}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Upload QR Code Image</label>
              <input type="file" accept="image/*" onChange={handleFileChange} className="w-full border px-3 py-2 rounded file:bg-blue-600 file:text-white file:rounded file:px-3 file:py-2"/>
            </div>
            <button type="submit" disabled={loading} className="h-10 mt-4 md:mt-0 px-5 py-2 rounded-lg bg-black hover:bg-green-600 text-white font-semibold shadow disabled:opacity-50 transition">
              {loading ? "Uploading..." : "Upload"}
            </button>
          </form>
          {message && <div className={`mt-4 text-center ${message.includes("success") ? "text-green-600" : "text-red-600"}`}>{message}</div>}
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6">
          <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white text-center">Existing Branch QR Codes</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {allBranchKeys.map(({ main, sub }) => {
              const key = makeBranchKey(main, sub);
              return (
                <div key={key} className="flex flex-col items-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-5 shadow hover:shadow-lg transition">
                  <div className="font-semibold text-gray-800 dark:text-white mb-2">
                    {main}{sub && <span className="text-xs ml-2 px-2 py-0.5 bg-blue-200 rounded-full">{sub}</span>}
                  </div>
                  {qrCodes[key] ? (
                    <img src={qrCodes[key].startsWith('http') ? qrCodes[key] : `https://japl-backend-v2jm.onrender.com/${qrCodes[key].replace(/^\//, '')}`} alt={`${main}${sub ? " - " + sub : ""} QR Code`} className="w-32 h-32 object-contain bg-white rounded shadow mb-2 border-2 border-blue-200" />
                  ) : (
                    <div className="w-32 h-32 flex items-center justify-center rounded bg-white border-2 text-gray-400 mb-2 border-gray-200">No QR Uploaded</div>
                  )}
                  {qrCodes[key] && (
                    <button onClick={() => handleDelete(main, sub)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded mt-2 transition text-sm">Delete</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminQRUpload;
